import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { DataGridItem } from "components/data-grid";
import { injectIntl, FormattedMessage } from "react-intl";
import FormTextField from "components/form/form-textfield";
import { CardWithDelete } from "components/card";
import withStyles from "components/with-styles";
import styles from "./styles/livestream-form.css.js";
import { IconRadioGroup, IconRadioButton } from "components/icon-radio-buttons";
import StaticIconRadioButton from "../../../../../components/static-icon-radio-button/static-icon-radio-button";
import {
  dailymotionIcon,
  dailymotionIconBlack,
  tiiviiIcon,
  tiiviiIconBlack,
  tiiviiIconDisabled,
  twitchIcon,
  twitchIconBlack,
  dailymotionIconDisabled,
  twitchIconDisabled,
} from "../../../../../config/paths";
import {
  EVENT_PAGE_PARAM_NEW,
  LIVESTREAM_TYPE_CUSTOM,
  LIVESTREAM_TYPE_TIIVII_OTT,
  SERVICES_METADATA,
  STREAM_STATUS_DELETED,
} from "./livestream-form-constants";
import { useSelector, useDispatch } from "react-redux";
import { storeTiiviiRtmpConfigActionCreator } from "../../../../../actions/common-actions.js";
import { validateRtmpUrl } from "../../utils/validateRtmpUrl";
import Typography from "@material-ui/core/Typography";

const getServiceFromUrl = livestream => {
  const emptyLiveStream = Object.values(livestream).every(value => {
    if (value === "") {
      return true;
    }
  });

  if (emptyLiveStream) {
    return LIVESTREAM_TYPE_CUSTOM;
  }

  const servicesHasRTMPURL = !!livestream.rtmpUrl
    ? Object.keys(SERVICES_METADATA).find(service => SERVICES_METADATA[service].url === livestream.rtmpUrl)
    : undefined;

  if (!!servicesHasRTMPURL) {
    return servicesHasRTMPURL;
  }

  const isOTTDescription = livestream.description === SERVICES_METADATA[LIVESTREAM_TYPE_TIIVII_OTT].description;

  return isOTTDescription ? SERVICES_METADATA[LIVESTREAM_TYPE_TIIVII_OTT].description : SERVICES_METADATA.custom.name;
};

const isEditableRtmpUrl = service =>
  service === SERVICES_METADATA.custom.name ||
  service === SERVICES_METADATA.youtube.name ||
  service === SERVICES_METADATA.twitch.name;

const LiveStreamForm = ({ name, classes, liveStream, handleChange, intl }) => {
  const isFirst = useRef(true);

  const path = name ? name + "." : "";
  const page = window.location.pathname.split("/")[2];

  const liveStreams = useSelector(state => state.commonState.liveStreams);
  const hasTiiviiOttConfig = useSelector(state => state.commonState.hasTiiviiOttConfig);

  const [service, setService] = useState(getServiceFromUrl(liveStream));
  const [editableRtmpUrl, setEditableRtmpUrl] = useState(isEditableRtmpUrl(service));
  const [canAddTiiviiOttStream, setCanAddTiiviiOttStream] = useState(null);
  const [livestreamKeyWarning, setLivestreamKeyWarning] = useState(false);

  const handleChangeRadioButton = id => {
    setService(id);
    setEditableRtmpUrl(isEditableRtmpUrl(id));
    if (SERVICES_METADATA.hasOwnProperty(id)) {
      handleChange(`${path}type`)(SERVICES_METADATA[id].type);
      handleChange(`${path}rtmpUrl`)(SERVICES_METADATA[id].url);
      handleChange(`${path}description`)(SERVICES_METADATA[id].description);
    }
  };

  const checkIfCanAddTiiviiOttStream = array => {
    let condition;

    if (page !== EVENT_PAGE_PARAM_NEW) {
      condition =
        array.filter(
          stream =>
            stream.description === LIVESTREAM_TYPE_TIIVII_OTT && stream.componentStatus !== STREAM_STATUS_DELETED,
        ).length === 0;
    } else {
      condition =
        (array.length > 1 && array.filter(stream => stream.type === LIVESTREAM_TYPE_TIIVII_OTT).length === 0) ||
        array.length <= 1;
    }

    const alreadyTiiviiOtt = array.find(
      stream => stream.type === LIVESTREAM_TYPE_TIIVII_OTT || stream.description === LIVESTREAM_TYPE_TIIVII_OTT,
    );
    const index = array.indexOf(alreadyTiiviiOtt);

    if (path === `liveStreams.${index}.`) {
      condition = true;
    }

    return condition;
  };

  useEffect(
    () => {
      if (isFirst.current) {
        handleChange(`${path}rtmpUrl`)(liveStream.rtmpUrl || SERVICES_METADATA[service].url);
        handleChange(`${path}description`)(liveStream.description || SERVICES_METADATA[service].description);

        if (service === LIVESTREAM_TYPE_TIIVII_OTT) {
          handleChange(`${path}type`)(SERVICES_METADATA[service].type);
          handleChange(`${path}description`)(SERVICES_METADATA[service].description);
        } else if (!liveStream.type && SERVICES_METADATA[service]?.type) {
          handleChange(`${path}type`)(SERVICES_METADATA[service].type);
        }

        isFirst.current = false;
      }
    },
    [handleChange, liveStream.description, liveStream.rtmpUrl, path, service],
  );

  useEffect(
    () => {
      setCanAddTiiviiOttStream(checkIfCanAddTiiviiOttStream(liveStreams));
    },
    [liveStreams, liveStream],
  );

  const dispatch = useDispatch();
  const isEmptyRtmpUrl = !(liveStream?.rtmpUrl !== "" && validateRtmpUrl(liveStream?.rtmpUrl));

  useEffect(
    () => {
      dispatch(storeTiiviiRtmpConfigActionCreator(isEmptyRtmpUrl));
    },
    [isEmptyRtmpUrl],
  );

  useEffect(
    () => {
      setService(getServiceFromUrl(liveStream));
    },
    [liveStream],
  );

  const validateLivestreamKey = (value) => {
    const trimmedKey = value.trim();

    if (!trimmedKey) {
      setLivestreamKeyWarning(false);
      return;
    }

    const regex = /^[a-zA-Z0-9_-]+$/;
    setLivestreamKeyWarning(!regex.test(trimmedKey));
  }

  return (
    <>
      <DataGridItem big>
        <div>
          <IconRadioGroup value={service} onChange={handleChangeRadioButton}>
            <IconRadioButton
              id={SERVICES_METADATA.youtube.name}
              label={intl.formatMessage({ id: "app.event.livestream.type.youtube" })}
              iconName="youtube"
              disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
            />
            <IconRadioButton
              id={SERVICES_METADATA.facebook.name}
              label={intl.formatMessage({ id: "app.event.livestream.type.facebookLive" })}
              iconName="facebook"
              disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
            />
            <IconRadioButton
              id={SERVICES_METADATA.vimeo.name}
              label={intl.formatMessage({ id: "app.event.livestream.type.vimeo" })}
              iconName="video-library"
              disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
            />
            <StaticIconRadioButton
              id={SERVICES_METADATA[LIVESTREAM_TYPE_TIIVII_OTT].name}
              label={intl.formatMessage({ id: "app.event.livestream.type.tiivii.ott" })}
              srcSelected={tiiviiIcon}
              srcDeselected={tiiviiIconBlack}
              srcDisabled={tiiviiIconDisabled}
              disabled={!hasTiiviiOttConfig || !canAddTiiviiOttStream || page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
            />
            <StaticIconRadioButton
              id={SERVICES_METADATA.twitch.name}
              label={intl.formatMessage({ id: "app.event.livestream.type.twitch" })}
              srcSelected={twitchIcon}
              srcDeselected={twitchIconBlack}
              srcDisabled={twitchIconDisabled}
              disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
            />
            <StaticIconRadioButton
              id={SERVICES_METADATA.dailymotion.name}
              label={intl.formatMessage({ id: "app.event.livestream.type.dailymotion" })}
              srcSelected={dailymotionIcon}
              srcDeselected={dailymotionIconBlack}
              srcDisabled={dailymotionIconDisabled}
              disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
            />
            <IconRadioButton
              id={SERVICES_METADATA.custom.name}
              label={intl.formatMessage({ id: "app.event.livestream.type.custom" })}
              iconName="build"
              disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
            />
          </IconRadioGroup>
        </div>
      </DataGridItem>
      {service === LIVESTREAM_TYPE_TIIVII_OTT ? (
        <div>
          <DataGridItem big>
            {hasTiiviiOttConfig && <span>{intl.formatMessage({ id: "app.event.livestream.tiivii.ott.message" })}</span>}
          </DataGridItem>
        </div>
      ) : (
        <>
          <DataGridItem big>
            <FormTextField
              type="text"
              required={true}
              disabled={!editableRtmpUrl}
              id={`${path}rtmpUrl`}
              name={`${path}rtmpUrl`}
              value={liveStream.rtmpUrl || ""}
              onChange={handleChange}
              label={intl.formatMessage({ id: "event.liveStream.rtmpUrl" })}
              InputLabelProps={{ shrink: true }}
            />
          </DataGridItem>
          <DataGridItem big>
            <FormTextField
              marginDense
              type="text"
              id={`${path}key`}
              name={`${path}key`}
              value={liveStream.key || ""}
              onChange={name => value => {
                validateLivestreamKey(value)
                handleChange(name)(value)
              }}
              label={intl.formatMessage({ id: "event.liveStream.key" })}
            />
            {livestreamKeyWarning && (
                <Typography variant="caption" color="textSecondary">{intl.formatMessage({id: 'event.key.novalid'})}</Typography>
            )}
          </DataGridItem>
          <DataGridItem big>
            <FormTextField
              marginDense
              type="text"
              id={`${path}description`}
              name={`${path}description`}
              value={liveStream.description || ""}
              onChange={handleChange}
              label={intl.formatMessage({ id: "common.description" })}
            />
          </DataGridItem>
          <DataGridItem big>
            <FormTextField
              marginDense
              type="text"
              id={`${path}publishingUrl`}
              name={`${path}publishingUrl`}
              value={liveStream.publishingUrl || ""}
              onChange={handleChange}
              label={intl.formatMessage({ id: "event.liveStream.publishingUrl" })}
            />
          </DataGridItem>
          <DataGridItem big>
            {(liveStream.rtmpUrl || liveStream.key) && (
              <div className={classes.finalUrl}>
                <FormattedMessage
                  id="event.livestream.finalUrl"
                  values={{
                    rtmpUrl: liveStream.rtmpUrl.trim().endsWith("/") ? liveStream.rtmpUrl : liveStream.rtmpUrl + "/",
                    key: liveStream.key,
                  }}
                />
              </div>
            )}
          </DataGridItem>
        </>
      )}
    </>
  );
};

LiveStreamForm.propTypes = {
  /**
   * Nombre del elemento de la colección
   */
  name: PropTypes.string,
  /**
   * Función que se invoca al modificar algún valor
   */
  handleChange: PropTypes.func,
  /**
   * Objeto con los valores que se quieren representar
   */
  liveStream: PropTypes.object,
};

const I18nLiveStreamForm = withStyles(styles)(injectIntl(LiveStreamForm));

const EventLiveStreamForm = ({ showInCard = true, onDelete, element, onChange, collectionName, index, ...props }) => {
  return showInCard ? (
    <CardWithDelete onDelete={onDelete}>
      <I18nLiveStreamForm
        name={collectionName ? `${collectionName}.${index}` : ""}
        handleChange={onChange}
        liveStream={element}
        {...props}
      />
    </CardWithDelete>
  ) : (
    <I18nLiveStreamForm
      name={collectionName ? `${collectionName}.${index}` : ""}
      handleChange={onChange}
      liveStream={element}
      {...props}
    />
  );
};

EventLiveStreamForm.propTypes = {
  /**
   * Indica si se muestra en una tarjeta
   */
  showInCard: PropTypes.bool,
  /**
   * Función que se invoca al eliminar el contacto del formulario
   */
  onDelete: PropTypes.func,
  /**
   ** Función que se invoca al editar un input
   **/
  onChange: PropTypes.func,
  /**
   ** Livestream to render
   **/
  element: PropTypes.object,
  /**
   * Índice del elemento en la colección
   */
  index: PropTypes.number,
  /**
   * Nombre de la colección
   */
  collectionName: PropTypes.string,
};

export default EventLiveStreamForm;
